import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IVendor, IVendorInput, vendorsAtom, vendorsLoadingAtom } from "./types";

type UseVendors = [IVendor[], {
    list: (query?: Record<string, any>) => Promise<IVendor[] | null>;
    getById: (id: string) => Promise<IVendor | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IVendor[] | null>;
    create: (input: IVendorInput) => Promise<IVendor | null>;
    update: (id: string, input: IVendorInput) => Promise<IVendor | null>;
    remove: (id: string) => Promise<IVendor | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useVendors = (): UseVendors => {
    const [items, setItems] = useAtom(vendorsAtom);
    const setLoading = useSetAtom(vendorsLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getVendors(query: $query){ id brand_name email phone website primary_contact created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getVendors);
            return data.getVendors as IVendor[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch vendors.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: string) => {
        try { const q = `query($id: ID!){ getVendorById(id:$id){ id brand_name email phone website primary_contact created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getVendorById as IVendor;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedVendors(first:$first,offset:$offset,query:$query){ id brand_name email phone website primary_contact created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedVendors as IVendor[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: IVendorInput) => {
        try { const q = `mutation($input:VendorInput!){ createVendor(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createVendor as IVendor;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: string, input: IVendorInput) => {
        try { const q = `mutation($id:ID!,$input:VendorInput!){ updateVendor(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateVendor as IVendor;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: string) => {
        try { const q = `mutation($id:ID!){ deleteVendor(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteVendor as IVendor;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


